import { Band, Eyebrow, SectionTitle, QUIET_LINK } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { useT } from '@/core/I18nProvider';
import { TRUST_BADGES } from '@/features/footer';

import { FAQ_MORE_HREF, LANDING_FAQ } from './trust-data';

import type { ReactElement } from 'react';

/**
 * Trust and objections — the six questions that block a non-developer buyer.
 *
 * ## Why it is back on the page
 *
 * The redesign rebuilt the landing band-by-band and dropped this section, but
 * kept `LANDING_FAQ` — `LandingPage` still fed it to `useLocalizedSeoMeta`. So
 * `/` emitted `FAQPage` structured data for six questions that appeared nowhere
 * on it, in all six locales. Google's FAQPage policy requires the content to be
 * visible on the page that declares it, so that was a live structured-data
 * violation as well as a lost section — and nothing could catch it: the data
 * was reachable, the barrel kept the component compiling, and no test asserts
 * that emitted JSON-LD corresponds to rendered text.
 *
 * Rendering it again is also the better answer on its own terms. These are the
 * objections a VP Product raises, answered where they raise them rather than in
 * `/docs/faq`, which a visitor evaluating the product has no reason to open.
 *
 * ## What changed in the rebuild
 *
 * Composed from bedrock instead of the retired `trust.css`, which had been
 * reading a token set (`--sx-line`, `--sx-txt`, `--sx-brand-400`, …) that was
 * deleted with the superseded landing sections — so its borders, its chevron
 * and its focus outline had all silently stopped resolving.
 *
 * Two things are deliberately unchanged. Answers are still reused verbatim from
 * the docs catalog (see `trust-data.ts`), so the page cannot soften a claim the
 * documentation states plainly. And the badges still render the same
 * `TRUST_BADGES` source both footers use — this is the section most likely to
 * attract an overclaim, so nothing in it is hand-written here.
 *
 * Native `<details>` rather than a scripted accordion: keyboard- and
 * screen-reader-correct with no JS, working before hydration, and reachable by
 * browser find-in-page even while closed.
 */
export function TrustObjections(): ReactElement {
  const { t } = useT();

  return (
    // `sunk` rather than the page ground: the band sits between two white ones
    // (Control and the closing CTA), and an unmarked boundary would let a long
    // read run into the close with nothing to separate them.
    //
    // `align="card"` puts this stripe's title on the same left rule as the
    // inset bands around it (how / control / closing). Without it the landing
    // page runs two left rules — see `BandAlign`.
    <Band id="trust" space="lg" tone="sunk" align="card" aria-labelledby="trust-title">
      <div className="max-w-[730px]">
        <Eyebrow>{t('landing.trust.eyebrow')}</Eyebrow>
        <SectionTitle id="trust-title" className="mt-3">
          {t('landing.trust.title')}
        </SectionTitle>
      </div>

      {/* Two columns once there is room for a 76ch read AND a badge rail beside
          it. Below that the badges land after the questions, where they always
          did. A plain `div` rather than an `aside`: a second unnamed
          complementary landmark inside one section is noise to a screen reader,
          and the list already carries its own label. */}
      <div
        data-bd-rise
        className="mt-10 grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_248px] lg:gap-14"
      >
        <div>
          <ul
            aria-label={t('landing.trust.faqLabel')}
            className="max-w-[76ch] border-t border-[color:var(--bd-line-2)]"
          >
            {LANDING_FAQ.map((entry, index) => (
              <li key={entry.questionKey} className="border-b border-[color:var(--bd-line-2)]">
                {/* `name` makes the six a native exclusive accordion — opening
                    one closes the rest, with no JS and no state. */}
                <details name="landing-faq" open={index === 0} className="group">
                  <summary
                    className="flex cursor-pointer list-none items-start justify-between gap-5 py-5 text-start
                      [&::-webkit-details-marker]:hidden
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:color-mix(in_srgb,var(--bd-blue)_45%,transparent)]"
                  >
                    <span className="text-pretty text-[1.02rem] font-medium leading-[1.45] tracking-[-0.015em] text-[color:var(--bd-ink)]">
                      {t(entry.questionKey)}
                    </span>
                    {/* A chevron drawn from two borders, so it needs no icon
                        payload and inherits the ink.

                        The `rtl:` pair is load-bearing and was missing at first.
                        A down-chevron is the BOTTOM and RIGHT edges of a square
                        rotated 45°, and `border-e` is logical — under RTL it
                        becomes the left edge, which rotates the arrow by 90°: in
                        Hebrew the six rows showed ‹ and › instead of ⌄ and ⌃.
                        Vertical disclosure is not a directional concept, so this
                        is one of the rare cases where the mirrored side has to
                        be pinned back. Written with logical utilities in both
                        directions rather than physical ones, so it still obeys
                        the no-physical-directional rule. */}
                    <span
                      aria-hidden="true"
                      className="mt-1.5 size-2.5 flex-none rotate-45 border-b-2 border-e-2 border-[color:var(--bd-subtle)]
                        rtl:border-e-0 rtl:border-s-2
                        transition-transform duration-200 group-open:-rotate-[135deg]"
                    />
                  </summary>
                  <p className="max-w-[68ch] pb-6 text-[0.95rem] leading-[1.68] text-[color:var(--bd-body)]">
                    {t(entry.answerKey)}
                  </p>
                </details>
              </li>
            ))}
          </ul>

          <p className="mt-7">
            <Link to={FAQ_MORE_HREF} className={`text-sm ${QUIET_LINK}`}>
              {t('landing.trust.moreQuestions')}{' '}
              <span aria-hidden="true" className="inline-block rtl:-scale-x-100">
                →
              </span>
            </Link>
          </p>
        </div>

        {/* Sticky on desktop only: the questions are an accordion, so the left
            column's height changes as the reader opens one, and the evidence
            should stay beside the objection being read rather than stranded at
            the top of a column that just grew. Cleared past the sticky bar with
            the same token every in-page anchor uses. */}
        <ul
          aria-label={t('footer.trustBadges')}
          className="flex flex-col gap-3 rounded-[var(--bd-radius)] border border-[color:var(--bd-line)] bg-[color:var(--bd-page)] p-6 lg:sticky lg:top-[calc(var(--bd-nav-h)+22px)]"
        >
          {TRUST_BADGES.map((badge) => (
            <li
              key={badge.labelKey}
              className="flex items-center gap-2.5 text-[0.83rem] leading-snug text-[color:var(--bd-muted)]"
            >
              <span
                aria-hidden="true"
                className="size-1.5 flex-none rounded-full"
                style={{
                  // In-progress claims are amber, not green: the badge rail is
                  // the one place on the page a compliance state could be
                  // over-read, so the two states must not look alike.
                  background: badge.tone === 'in-progress' ? 'var(--bd-warn)' : 'var(--bd-ok)',
                }}
              />
              {t(badge.labelKey)}
            </li>
          ))}
          <li className="mt-1 border-t border-[color:var(--bd-line)] pt-3.5">
            <Link to="/trust" className={`text-[0.83rem] ${QUIET_LINK}`}>
              {t('landing.trust.seeTrustCenter')}{' '}
              <span aria-hidden="true" className="inline-block rtl:-scale-x-100">
                →
              </span>
            </Link>
          </li>
        </ul>
      </div>
    </Band>
  );
}
